import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Inbox, FolderKanban, Wrench, Users, MapPin, UserCog, Hash, Quote, Image, Info, Settings, LogOut, Menu, X, ExternalLink } from 'lucide-react';
import { adminMe, clearToken, getToken } from '@/lib/api';
import { BrandLogo } from '@/components/BrandLogo';
import { Loader } from '@/components/Loader';

const CONTENT = [
  { to: '/admin/content/projects', label: 'Projects', icon: FolderKanban },
  { to: '/admin/content/services', label: 'Services', icon: Wrench },
  { to: '/admin/content/equipment', label: 'Equipment', icon: Wrench },
  { to: '/admin/content/clients', label: 'Clients', icon: Users },
  { to: '/admin/content/locations', label: 'Locations', icon: MapPin },
  { to: '/admin/content/leadership', label: 'Leadership', icon: UserCog },
  { to: '/admin/content/metrics', label: 'Metrics', icon: Hash },
  { to: '/admin/content/testimonials', label: 'Testimonials', icon: Quote },
];
const SECTIONS = [
  { to: '/admin/singleton/hero', label: 'Hero', icon: Image },
  { to: '/admin/singleton/about', label: 'About / Vision', icon: Info },
  { to: '/admin/singleton/site', label: 'Site & Contact', icon: Settings },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
    isActive ? 'bg-[#075B61] text-white' : 'text-[#20272A]/70 hover:bg-[#075B61]/8 hover:text-[#075B61]'
  }`;

export default function AdminLayout() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate('/admin'); return; }
    adminMe().then(() => setChecking(false)).catch(() => { clearToken(); navigate('/admin'); });
  }, [navigate]);

  const logout = () => { clearToken(); navigate('/admin'); };

  if (checking) return <Loader label='Authenticating' />;

  const Sidebar = (
    <div className='flex h-full flex-col'>
      <div className='px-5 py-5'><BrandLogo height={34} variant='color' /></div>
      <nav className='flex-1 space-y-1 overflow-y-auto px-3 pb-6'>
        <NavLink to='/admin/dashboard' className={linkCls} onClick={() => setOpen(false)}><LayoutDashboard size={17} /> Dashboard</NavLink>
        <NavLink to='/admin/enquiries' className={linkCls} onClick={() => setOpen(false)}><Inbox size={17} /> Enquiries</NavLink>
        <div className='px-3 pb-1 pt-4 text-[10px] uppercase tracking-wider text-[#20272A]/35'>Content</div>
        {CONTENT.map((l) => <NavLink key={l.to} to={l.to} className={linkCls} onClick={() => setOpen(false)}><l.icon size={17} /> {l.label}</NavLink>)}
        <div className='px-3 pb-1 pt-4 text-[10px] uppercase tracking-wider text-[#20272A]/35'>Sections</div>
        {SECTIONS.map((l) => <NavLink key={l.to} to={l.to} className={linkCls} onClick={() => setOpen(false)}><l.icon size={17} /> {l.label}</NavLink>)}
      </nav>
      <div className='border-t border-[#20272A]/10 p-3'>
        <a href='/' target='_blank' rel='noreferrer' className='flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-[#20272A]/70 hover:text-[#075B61]'><ExternalLink size={17} /> View Website</a>
        <button onClick={logout} className='flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50' data-testid='admin-logout-button'><LogOut size={17} /> Log Out</button>
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-[#F6F5F1]'>
      {/* mobile top bar */}
      <div className='sticky top-0 z-30 flex items-center justify-between border-b border-[#20272A]/10 bg-white px-4 py-3 lg:hidden'>
        <BrandLogo height={30} variant='color' />
        <button onClick={() => setOpen(true)} className='inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#20272A]/10 text-[#20272A]'><Menu size={20} /></button>
      </div>

      <div className='lg:flex'>
        <aside className='hidden w-64 shrink-0 border-r border-[#20272A]/10 bg-white lg:block lg:h-screen lg:sticky lg:top-0'>{Sidebar}</aside>

        {open && (
          <div className='fixed inset-0 z-50 lg:hidden'>
            <div className='absolute inset-0 bg-black/40' onClick={() => setOpen(false)} />
            <div className='absolute left-0 top-0 h-full w-72 bg-white'>
              <button onClick={() => setOpen(false)} className='absolute right-3 top-4 text-[#20272A]/50'><X size={20} /></button>
              {Sidebar}
            </div>
          </div>
        )}

        <main className='min-w-0 flex-1 p-5 sm:p-8 lg:p-10'><Outlet /></main>
      </div>
    </div>
  );
}
